import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Custom marker icon (fixes missing default icon issue in Leaflet)
const markerIcon = new L.Icon({
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    shadowSize: [41, 41],
});

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: 600,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        margin: '20px 0',
        overflow: 'hidden',
    },
    map: {
        flex: 1,
        minWidth: 0,
        height: '100%',
        position: 'relative',
    },
    sidePanel: {
        width: 260,
        background: '#f9f9f9',
        borderLeft: '1px solid #eee',
        padding: '24px 16px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        fontSize: 20,
        fontWeight: 500,
        color: '#3A3E52',
        marginBottom: 16,
    },
    cityList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    cityItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #eee',
        fontSize: 16,
        color: '#333',
    },
    cityName: {
        fontWeight: 500,
    },
    cityCount: {
        background: '#4A90E2',
        color: '#fff',
        borderRadius: 12,
        padding: '2px 10px',
        fontSize: 14,
        fontWeight: 600,
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    loadingSpinner: {
        width: 40,
        height: 40,
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #4A90E2',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    loadingText: {
        marginTop: 16,
        fontSize: 16,
        color: '#666',
        textAlign: 'center',
    },
    errorOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
    },
    errorIcon: {
        fontSize: 48,
        color: '#ff6b6b',
        marginBottom: 16,
    },
    errorText: {
        fontSize: 16,
        color: '#666',
        textAlign: 'center',
        marginBottom: 16,
    },
    retryButton: {
        background: '#4A90E2',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        padding: '10px 20px',
        fontSize: 14,
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    retryButtonHover: {
        background: '#357abd',
    },
    fallbackMap: {
        width: '100%',
        height: '100%',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontSize: 16,
    },
};

const MapSection = ({ data = [], title = 'Listeners by Region', onRetry, isLoading: externalLoading = false }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [mapKey, setMapKey] = useState(0); // Force map re-render on retry

    // Center the map on the US
    const center = [39.8283, -98.5795];

    // Maximum retry attempts
    const MAX_RETRIES = 3;

    useEffect(() => {
        // Reset states when data changes
        setIsLoading(true);
        setHasError(false);
        
        // If external loading is true, show loading immediately
        if (externalLoading) {
            setIsLoading(true);
            return;
        }
        
        // Simulate loading time for coordinate processing
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, [data, externalLoading]);

    const handleRetry = () => {
        if (retryCount < MAX_RETRIES) {
            setRetryCount(prev => prev + 1);
            setMapKey(prev => prev + 1);
            setIsLoading(true);
            setHasError(false);
            
            // Call parent retry function if provided
            if (onRetry) {
                onRetry();
            }
        }
    };

    const handleMapError = () => {
        setIsLoading(false);
        setHasError(true);
    };

    // Add CSS animation for spinner
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <div style={styles.container}>
            <div style={styles.map}>
                {(isLoading || externalLoading) && (
                    <div style={styles.loadingOverlay}>
                        <div style={styles.loadingSpinner}></div>
                        <div style={styles.loadingText}>
                            {externalLoading ? 'Refreshing map data...' : 'Loading map coordinates...'}
                            {retryCount > 0 && ` (Retry ${retryCount}/${MAX_RETRIES})`}
                        </div>
                    </div>
                )}
                
                {hasError && (
                    <div style={styles.errorOverlay}>
                        <div style={styles.errorIcon}>⚠️</div>
                        <div style={styles.errorText}>
                            Failed to load map coordinates.<br />
                            The coordinate file (6MB) may be taking longer to load.
                        </div>
                        {retryCount < MAX_RETRIES ? (
                            <button 
                                style={styles.retryButton}
                                onClick={handleRetry}
                                onMouseEnter={(e) => e.target.style.background = '#357abd'}
                                onMouseLeave={(e) => e.target.style.background = '#4A90E2'}
                            >
                                Retry ({MAX_RETRIES - retryCount} attempts left)
                            </button>
                        ) : (
                            <div style={styles.errorText}>
                                Maximum retry attempts reached. Please refresh the page.
                            </div>
                        )}
                    </div>
                )}

                {!isLoading && !externalLoading && !hasError && (
                    <MapContainer 
                        key={mapKey}
                        center={center} 
                        zoom={4} 
                        style={{ width: '100%', height: '100%' }} 
                        scrollWheelZoom={true}
                        whenReady={() => setIsLoading(false)}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                            eventHandlers={{
                                error: handleMapError,
                                load: () => setIsLoading(false)
                            }}
                        />
                        {data.map((marker, idx) => (
                            <Marker key={idx} position={marker.position} icon={markerIcon}>
                                <Popup>
                                    <div>
                                        <strong>{marker.city}</strong><br />
                                        Listeners: {marker.metrics || marker.uniqueListeners}
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                )}

                {!isLoading && !externalLoading && !hasError && (!data || data.length === 0) && (
                    <div style={styles.fallbackMap}>
                        No location data available for the selected period
                    </div>
                )}
            </div>
            
            <div style={styles.sidePanel}>
                <div style={styles.title}>Top Listener Cities</div>
                <ul style={styles.cityList}>
                    {data && data.length > 0 ? (
                        data.slice(0, 10).map((city, idx) => (
                            <li key={idx} style={styles.cityItem}>
                                <span style={styles.cityName}>{city.city}</span>
                                <span style={styles.cityCount}>
                                    {city.metrics || city.uniqueListeners}
                                </span>
                            </li>
                        ))
                    ) : (
                        <li style={{ ...styles.cityItem, justifyContent: 'center', color: '#888' }}>
                            No data available
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default MapSection; 